import React, { useState, useRef } from 'react';
import tw, { TwStyle } from 'twin.macro';

import { CaretIcon } from '@icons/CaretIcon';
import useOnClickOutside from '@app/hooks/useOnClickOutside';
import { UserRoleType } from '@account_module/types/Organizations';

const SelectLabel = tw.button`text-[11px] min-w-[75px] w-auto border-none py-[5px] px-2 capitalize rounded flex items-center justify-between`;
const OptionsContainer = tw.div`absolute bg-neutral-background rounded p-1 border border-solid border-gray-1 w-[232px] mt-1 z-50 shadow-dropdown`;
const Option = tw.button`cursor-pointer text-left p-[6px] hover:bg-gray-3 rounded`;
const OptionTitle = tw.span`text-left text-xs text-text-and-icon-muted font-semibold mb-0.5`;
const OptionDescription = tw.p`text-left text-[11px] text-text-and-icon-muted tracking-[0.11px leading-[140%] font-normal]`;

export type OptionType = {
  key: UserRoleType;
  label: string;
  title: string;
  description: string;
};

export const RoleSelect = ({
  onChange,
  options,
  labelStyle,
  staticLabel,
  disabled = false,
}: {
  onChange: (val: UserRoleType) => void;
  options: OptionType[];
  labelStyle?: TwStyle;
  staticLabel?: string;
  disabled?: boolean;
}) => {
  const optionsRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<OptionType>(options[0]);
  useOnClickOutside(optionsRef, () => setIsExpanded(false));

  const handleChange = (e: React.MouseEvent<HTMLButtonElement>, val: OptionType) => {
    e.preventDefault();
    setSelectedOption(val);
    onChange(val.key);

    setTimeout(() => {
      setIsExpanded(false);
    }, 10);
  };

  return (
    <div ref={optionsRef}>
      <SelectLabel
        type='button'
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
        css={[labelStyle, disabled && tw`opacity-40`]}>
        {staticLabel ? <span tw='inline-block mr-1'>{staticLabel}</span> : selectedOption.label}
        <CaretIcon tw='fill-text-and-icon-muted' />
      </SelectLabel>
      {isExpanded && (
        <OptionsContainer>
          {options.map((option) => (
            <Option
              key={option.key}
              onClick={(e) => handleChange(e as React.MouseEvent<HTMLButtonElement>, option)}>
              <OptionTitle>{option.title}</OptionTitle>
              <OptionDescription>{option.description}</OptionDescription>
            </Option>
          ))}
        </OptionsContainer>
      )}
    </div>
  );
};
